import { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { RowListSkeleton } from "@/components/RowListSkeleton";
import { FormActions, Toolbar } from "@jbnl/shared-ui";
import { VolgordeKnoppen } from "@/components/VolgordeKnoppen";
import { verplaats } from "@/lib/volgorde";
import {
  useCreateNiveau,
  useDeleteNiveau,
  useReorderNiveaus,
  useStamNiveaus,
  useUpdateNiveau,
  type NiveauStam,
} from "./useStamgegevens";

/**
 * The order is presentation and nothing else: ADR-0021 rejected a staffing rule keyed on it, so
 * moving a niveau cannot silently change what fully staffed means. The speelschema's have their
 * own screen, because a page called "Niveaus" is where they went unfound.
 */
export function Niveaus() {
  const { t } = useTranslation("stamgegevens");
  const { data: niveaus, isPending } = useStamNiveaus();
  const reorder = useReorderNiveaus();

  function verzet(index: number, delta: number) {
    reorder.mutate(verplaats(niveaus ?? [], index, delta).map((n) => n.id));
  }

  return (
    <>
      <Toolbar>
        <NieuwNiveau />
      </Toolbar>

      <Card>
        <CardContent className="space-y-3 pt-6">
          {isPending ? (
            <RowListSkeleton actions={["w-20", "w-24", "size-8", "size-8"]} />
          ) : (
            <ul className="divide-y">
              {niveaus?.map((niveau, index) => (
                <li
                  key={niveau.id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <span className="font-medium">{niveau.name}</span>
                  <span className="flex shrink-0 items-center gap-1">
                    <WijzigNiveau niveau={niveau} />
                    {!niveau.inUse && <VerwijderNiveau niveau={niveau} />}
                    <VolgordeKnoppen
                      label={niveau.name}
                      eerste={index === 0}
                      laatste={index === (niveaus?.length ?? 0) - 1}
                      disabled={reorder.isPending}
                      onOmhoog={() => verzet(index, -1)}
                      onOmlaag={() => verzet(index, 1)}
                    />
                  </span>
                </li>
              ))}
            </ul>
          )}

          <p className="text-sm text-muted-foreground">
            {t("niveaus.volgordeUitleg")}
          </p>

          {reorder.error && (
            <p className="text-sm text-destructive">{reorder.error.message}</p>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function NieuwNiveau() {
  const { t } = useTranslation("stamgegevens");
  const create = useCreateNiveau();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus />
          {t("niveaus.toevoegen")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("niveaus.toevoegen")}</DialogTitle>
        </DialogHeader>
        <NiveauVelden
          key={String(open)}
          submitLabel={t("niveaus.submitToevoegen")}
          pending={create.isPending}
          error={create.error}
          onSubmit={(waarden) =>
            create.mutate(waarden, { onSuccess: () => setOpen(false) })
          }
        />
      </DialogContent>
    </Dialog>
  );
}

function WijzigNiveau({ niveau }: { niveau: NiveauStam }) {
  const { t } = useTranslation(["stamgegevens", "common"]);
  const update = useUpdateNiveau();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          {t("niveaus.wijzigen")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("niveaus.wijzigTitle", { naam: niveau.name })}
          </DialogTitle>
        </DialogHeader>
        <NiveauVelden
          key={String(open)}
          niveau={niveau}
          submitLabel={t("common:actions.save")}
          pending={update.isPending}
          error={update.error}
          onSubmit={(waarden) =>
            update.mutate(
              { id: niveau.id, ...waarden },
              { onSuccess: () => setOpen(false) },
            )
          }
        />
      </DialogContent>
    </Dialog>
  );
}

function VerwijderNiveau({ niveau }: { niveau: NiveauStam }) {
  const { t } = useTranslation("stamgegevens");
  const remove = useDeleteNiveau();

  return (
    <ConfirmDelete
      trigger={
        <Button variant="ghost" size="sm" disabled={remove.isPending}>
          {t("niveaus.verwijderen")}
        </Button>
      }
      title={t("niveaus.verwijderVraag", { naam: niveau.name })}
      description={t("niveaus.verwijderDescription")}
      confirmLabel={t("niveaus.verwijderConfirm", { naam: niveau.name })}
      onConfirm={() => remove.mutate(niveau.id)}
    />
  );
}

function NiveauVelden({
  niveau,
  submitLabel,
  pending,
  error,
  onSubmit,
}: {
  niveau?: NiveauStam;
  submitLabel: string;
  pending: boolean;
  error: Error | null;
  onSubmit: (waarden: { name: string }) => void;
}) {
  const { t } = useTranslation(["stamgegevens", "common"]);
  const [name, setName] = useState(niveau?.name ?? "");

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ name });
      }}
    >
      <Field>
        <FieldLabel htmlFor="niveau-naam">{t("niveaus.naam")}</FieldLabel>
        <Input
          id="niveau-naam"
          value={name}
          maxLength={120}
          required
          onChange={(e) => setName(e.target.value)}
        />
        <FieldDescription>{t("niveaus.naamUitleg")}</FieldDescription>
      </Field>

      <FormActions>
        {error && <p className="text-sm text-destructive">{error.message}</p>}
        <Button type="submit" disabled={pending}>
          {pending ? t("common:actions.busy") : submitLabel}
        </Button>
      </FormActions>
    </form>
  );
}
